import {
  Component, ElementRef, EventEmitter, Input, Output, ViewChild,
  OnChanges, SimpleChanges
} from '@angular/core';
import { GridRound } from '../../../interfaces/treasure-grid.interface';

interface Sparkle { x: number; y: number; r: number; delay: number; }
interface Coin { tx: number; ty: number; rot: number; r: number; delay: number; gem: boolean; }

/**
 * Treasure Grid play surface — a first-quadrant coordinate grid on a nautical
 * sea chart. The child drags a marker that snaps to integer grid points; live
 * crosshair guides + a colour-coded (x, y) readout show where it is (x → cyan
 * / across, y → coral / up). DIG (triggered by the shell) checks the marker
 * against the target: a hit reveals treasure, a miss puffs dust.
 *
 * All positions are integer grid units so snapping/comparison is exact.
 */
@Component({
  selector: 'app-treasure-grid-stage',
  templateUrl: './treasure-grid-stage.component.html',
  styleUrls: ['./treasure-grid-stage.component.scss']
})
export class TreasureGridStageComponent implements OnChanges {
  @Input() round: GridRound | null = null;
  @Input() roundKey = 0;
  @Input() interactive = true;
  @Input() currLang: any = 'en';

  @Output() solved = new EventEmitter<void>();
  @Output() missed = new EventEmitter<void>();

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  readonly VIEW_W = 1000;
  readonly VIEW_H = 560;
  readonly OX = 500;          // pixel x of the origin (0,0) — grid centre
  readonly OY = 280;          // pixel y of the origin (0,0) — grid centre
  readonly HALF_W = 452;      // max grid half-width in px (room for arrows)
  readonly HALF_H = 230;      // max grid half-height in px
  readonly MAX_CELL = 60;     // don't zoom tiny grids in too far

  readonly X_CLR = '#3ad6e6'; // x — cyan (across)
  readonly Y_CLR = '#ff9269'; // y — coral (up)

  /** Current marker grid position. */
  gx = 0;
  gy = 0;
  placed = false;             // has the child moved the marker off origin yet
  solvedState = false;
  wrongFlash = false;
  sparkles: Sparkle[] = [];
  coins: Coin[] = [];
  /** Fixed sunburst ray angles (degrees). */
  readonly rayAngles = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];

  private dragging = false;
  private timers: any[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['round'] || changes['roundKey']) this.reset();
  }

  private reset(): void {
    this.clearTimers();
    this.solvedState = false;
    this.wrongFlash = false;
    this.dragging = false;
    this.placed = false;
    this.sparkles = [];
    this.coins = [];
    this.gx = 0;
    this.gy = 0;
  }

  // ── Geometry ───────────────────────────────────────────────────────

  get maxX(): number { return this.round?.maxX ?? 6; }
  get maxY(): number { return this.round?.maxY ?? 4; }

  /** Pixels per grid unit — sized so the round's range fits the viewport. */
  get CELL(): number {
    return Math.min(this.HALF_W / this.maxX, this.HALF_H / this.maxY, this.MAX_CELL);
  }
  /** Axis-number font, scaled down for denser (larger-range) grids. */
  get labelFont(): number { return Math.max(13, Math.min(24, this.CELL * 0.42)); }
  /** Value-flag width, sized to the readout text (handles "(-11, -7)"). */
  get readoutW(): number { return this.readoutLabel.length * 13 + 22; }

  px(x: number): number { return this.OX + x * this.CELL; }
  py(y: number): number { return this.OY - y * this.CELL; }
  get markerX(): number { return this.px(this.gx); }
  get markerY(): number { return this.py(this.gy); }
  get gridLeft(): number { return this.px(-this.maxX); }
  get gridRight(): number { return this.px(this.maxX); }
  get gridTop(): number { return this.py(this.maxY); }
  get gridBottom(): number { return this.py(-this.maxY); }

  /** Tick values run −max…+max (0 included, for the grid lines). */
  get xTicks(): number[] { return Array.from({ length: 2 * this.maxX + 1 }, (_, i) => i - this.maxX); }
  get yTicks(): number[] { return Array.from({ length: 2 * this.maxY + 1 }, (_, i) => i - this.maxY); }
  /** Number labels skip 0 (the origin gets its own single "0"). */
  get xLabels(): number[] { return this.xTicks.filter(v => v !== 0); }
  get yLabels(): number[] { return this.yTicks.filter(v => v !== 0); }

  /** Flip the value flag below the marker on the top row so it never clips
   *  above the panel. */
  get readoutDy(): number { return this.gy >= this.maxY ? 58 : -58; }

  get target(): GridRound | null { return this.round; }
  get onTarget(): boolean {
    return !!this.round && this.gx === this.round.tx && this.gy === this.round.ty;
  }
  get readoutLabel(): string { return `(${this.gx}, ${this.gy})`; }

  // ── Drag ───────────────────────────────────────────────────────────

  onDown(evt: PointerEvent): void {
    if (!this.interactive || this.solvedState) return;
    evt.preventDefault();
    this.dragging = true;
    this.wrongFlash = false;
    this.updateFromEvent(evt);
    try { this.svgRef.nativeElement.setPointerCapture(evt.pointerId); } catch { /* older TVs */ }
  }

  onMove(evt: PointerEvent): void {
    if (!this.dragging) return;
    this.updateFromEvent(evt);
  }

  onUp(evt: PointerEvent): void {
    if (!this.dragging) return;
    this.dragging = false;
    try { this.svgRef.nativeElement.releasePointerCapture(evt.pointerId); } catch { /* ignore */ }
  }

  private updateFromEvent(evt: PointerEvent): void {
    const p = this.toSvg(evt);
    const nx = Math.round((p.x - this.OX) / this.CELL);
    const ny = Math.round((this.OY - p.y) / this.CELL);
    this.gx = Math.max(-this.maxX, Math.min(this.maxX, nx));
    this.gy = Math.max(-this.maxY, Math.min(this.maxY, ny));
    this.placed = true;
  }

  // ── Dig (called by the shell) ──────────────────────────────────────

  get canDig(): boolean {
    return this.interactive && !this.solvedState && this.placed;
  }

  dig(): void {
    if (!this.round || this.solvedState) return;
    if (this.onTarget) {
      this.solvedState = true;
      this.spawnTreasure();
      this.solved.emit();
    } else {
      this.wrongFlash = true;
      this.timers.push(setTimeout(() => { this.wrongFlash = false; }, 620));
      this.missed.emit();
    }
  }

  /** Spawn the treasure-found burst: sparkles + a scatter of coins & gems,
   *  all as offsets from the marker (the win group is translated there). */
  private spawnTreasure(): void {
    this.sparkles = Array.from({ length: 14 }, (_, i) => {
      const a = (i / 14) * Math.PI * 2;
      const dist = 30 + (i % 3) * 18;
      return { x: Math.cos(a) * dist, y: Math.sin(a) * dist, r: 4 + (i % 3) * 3, delay: (i % 5) * 35 };
    });
    this.coins = Array.from({ length: 18 }, (_, i) => {
      const a = (i / 18) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      const dist = 70 + Math.random() * 78;
      return {
        tx: Math.cos(a) * dist,
        ty: Math.sin(a) * dist - 26,       // slight upward bias — a fountain
        rot: Math.random() * 720 - 360,
        r: 7 + Math.random() * 5,
        delay: Math.random() * 150,
        gem: i % 3 === 0
      };
    });
  }

  private toSvg(evt: PointerEvent): { x: number; y: number } {
    const svg = this.svgRef.nativeElement;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  }

  private clearTimers(): void {
    this.timers.forEach(t => clearTimeout(t));
    this.timers = [];
  }
}
